import type { ThreadEntry } from "../../api/threads/liveMessages";

/**
 * Which entries of a Thread's path can become steps of a saved Workflow (R-8): a turn that
 * completed (a step it ran, or a free reply whose messages become a prompt) and a human
 * step that was answered. Pure: the dialog lists them in path order.
 */
export interface SaveCandidate {
  readonly entryNumber: number;
  readonly kind: "step_turn" | "reply_turn" | "human_step";
  /** The step key it ran under, if any. */
  readonly stepKey: string | null;
  /** The first message the reply covered, for a reply turn. */
  readonly preview: string;
  /** Human steps cost nothing, so they start automatically by default (W-11). */
  readonly defaultStart: "automatic" | "on_request";
}

const PREVIEW_CHARACTERS = 80;

export function saveCandidates(entries: readonly ThreadEntry[]): readonly SaveCandidate[] {
  const completedTurns = new Set<string>();
  const answeredSteps = new Set<number>();
  const messages = new Map<number, string>();
  for (const entry of entries) {
    const payload = entry.payload;
    if (payload.kind === "turn_finished" && payload.outcome === "completed") {
      completedTurns.add(payload.turn_id);
    } else if (payload.kind === "human_input") {
      answeredSteps.add(payload.answers_entry);
    } else if (payload.kind === "message") {
      messages.set(entry.number, payload.text);
    }
  }
  const candidates: SaveCandidate[] = [];
  for (const entry of entries) {
    const payload = entry.payload;
    if (payload.kind === "turn_requested" && completedTurns.has(payload.turn_id)) {
      const reply = payload.turn_kind === "reply";
      const firstCovered = payload.covers.map((number) => messages.get(number)).find((text) => text !== undefined) ?? "";
      candidates.push({
        entryNumber: entry.number,
        kind: reply ? "reply_turn" : "step_turn",
        stepKey: payload.step_key ?? null,
        preview: firstCovered.slice(0, PREVIEW_CHARACTERS),
        defaultStart: "on_request",
      });
    } else if (payload.kind === "human_step_requested" && answeredSteps.has(entry.number)) {
      candidates.push({ entryNumber: entry.number, kind: "human_step", stepKey: payload.step_key, preview: "", defaultStart: "automatic" });
    }
  }
  return candidates;
}
